import { useUserContext } from '@/api/hooks/useUserContext';
import { useT } from '@/i18n';
import { useAuthStore } from '@/stores/auth.store';
import { useRestaurantStore } from '@/stores/restaurant.store';
import {
  BlockFooter,
  BlockTitle,
  List,
  ListInput,
  ListItem,
  NavLeft,
  NavTitle,
  Navbar,
  Page,
} from 'framework7-react';

// Role enums read like SCREAMING_CASE on the wire; show them title-cased.
function prettyRole(role: string | null): string {
  // null = franchise-wide access (partner-group member or system admin).
  if (!role) return 'Full access';
  return role
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export default function MyProfilePage() {
  const t = useT();
  const email = useAuthStore((s) => s.email);
  const { data: ctx } = useUserContext();
  const { selectedRestaurantId, selectedFranchiseId, userContext } = useRestaurantStore();

  // The role + restaurant for the currently-selected restaurant: find the
  // worker→restaurant access row that matches the active selection.
  const resolved = ctx ?? userContext;
  const access = resolved?.workers
    .flatMap((w) => w.restaurants.map((r) => ({ ...r, franchiseId: w.franchiseId })))
    .find((r) => r.restaurantId === selectedRestaurantId && r.franchiseId === selectedFranchiseId);

  return (
    <Page name="my-profile">
      <Navbar>
        <NavLeft backLink="Back" />
        <NavTitle>{t.profile.title}</NavTitle>
      </Navbar>

      <BlockTitle>{t.profile.personalInfo}</BlockTitle>
      <BlockFooter style={{ marginTop: -6 }}>{t.profile.contactAdminNote}</BlockFooter>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListInput
          label={t.profile.firstName}
          type="text"
          disabled
          placeholder={t.profile.notAvailableYet}
        />
        <ListInput
          label={t.profile.lastName}
          type="text"
          disabled
          placeholder={t.profile.notAvailableYet}
        />
        <ListItem title={t.profile.email} after={email ?? '-'} />
      </List>

      <BlockTitle>{t.profile.currentRestaurantRole}</BlockTitle>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListInput
          label={t.profile.nameInRestaurant}
          type="text"
          disabled
          placeholder={t.profile.notAvailableYet}
        />
        <ListItem title={t.profile.restaurant} after={access?.restaurantName ?? '-'} />
        <ListItem title={t.profile.role} after={access ? prettyRole(access.role) : '-'} />
      </List>
    </Page>
  );
}
